import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import authService from "../services/auth-service";
import roleService from "../services/role-service";
import IRole from "../types/role-type";
import IUser from "../types/user-type";

type Children = React.ReactNode;

interface UserContextType {
  user: IUser | undefined;
  userRole: string;
  userId: string;
  isLoading: boolean;
  handleLogin: (username: string, password: string) => Promise<void>;
  handleLogout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: Children }) => {
  const [user, setUser] = useState<IUser | undefined>(undefined);
  const [userRole, setUserRole] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const fetchUserRole = useCallback(async (userRoleId: string) => {
    try {
      console.log("Debug: Fetching user role!");
      const response = await roleService.getAllRoles();
      const roles = response.data;
      if (roles) {
        const userRoleName = roles.find((r: IRole) => r.id === userRoleId)?.name;
        return userRoleName;
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  }, []);

  const fetchUserData = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log("Debug: Fetching user data!");
      const userData: IUser = await authService.getCurrentUser();
      if (userData?.id && userData?.roles) {
        setUser(userData);
        setUserId(userData.id)
        const userRole = await fetchUserRole(userData.roles[0]);
        setUserRole(userRole);
      }
      setIsLoading(false);
    } catch (err) {
      console.log("Error fetching user data", err);
    }
  }, [fetchUserRole]);

  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem("isLoggedIn");
    if (isLoggedIn === "true" && user === undefined) {
      console.log(isLoggedIn, user)
      console.log("Debug: UseEffect");
      fetchUserData();
    }
  }, [fetchUserData, user]);

  const handleLogin = useCallback(
    async (username: string, password: string) => {
      try {
        console.log("Handlelogin!")
        setIsLoggingOut(false);
        const loginResponse = await authService.login(username, password);
        if (loginResponse === "Login successful") {
          toast.success("Login successful", { position: "bottom-right" });
        }
        await Promise.all([fetchUserData()]);
        sessionStorage.setItem("isLoggedIn", JSON.stringify(true));
      } catch (error) {
        toast.error("Login failed", { position: "bottom-right" });
        console.error("Login error", error);
      }
    },
    [fetchUserData]
  );

  const handleLogout = useCallback(() => {
    if (isLoggingOut) {
      return;
    }
    setIsLoggingOut(true);
    authService.logout()
      .then(() => {
        console.log("Debug: Logging out!");
        sessionStorage.setItem("isLoggedIn", JSON.stringify(false));
        sessionStorage.removeItem("userRole");
        sessionStorage.removeItem("userId");
        setUser(undefined);
        setUserId("");
        setUserRole("");
        window.location.reload()
        toast.info("Logged out successfully", { position: "bottom-right" });
      })
      .catch((error) => {
        console.error("Error during logout:", error);
        setIsLoggingOut(false); // Reset the flag even if there's an error
      })
      .finally(() => {
        setIsLoggingOut(false);
      });
  }, [isLoggingOut]);

  return (
    <UserContext.Provider
      value={{
        user,
        userId,
        userRole,
        isLoading,
        handleLogin,
        handleLogout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("Context has not been found");
  }
  return context;
};
